// ---------- Business matching ----------

// Turns any web address into its plain domain, using the same rule as the Worker.
// "https://www.checkatrade.com› ... › Plumber in Sheffield" -> "checkatrade.com"
// Only reads the address - never visits it. Google's own links are skipped.
export function domainOf(...candidates) {
  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== "string") continue;
    const cleaned = candidate.split("›")[0].trim();
    if (!cleaned || cleaned.startsWith("/")) continue;
    try {
      const host = new URL(cleaned.startsWith("http") ? cleaned : `https://${cleaned}`)
        .hostname.toLowerCase().replace(/^www\./, "");
      if (!host || !host.includes(".")) continue;
      if (host.includes("googleadservices") || /(^|\.)google\.[a-z.]+$/.test(host)) continue;
      return host;
    } catch (e) {
      // Not a usable address - try the next candidate.
    }
  }
  return null;
}

// The name a PPC or SEO result is grouped under.
export function siteName(item) {
  return item._domain || domainOf(item.url, item.url_shown) || "Unknown";
}

// "JB & Son Plumbing" -> "jbandsonplumbing"
export function compact(text) {
  return String(text || "").toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]/g, "");
}

// UK endings made of two parts. Any other ending (.com, .uk, .co, .io ...)
// is treated as one part.
const UK_TWO_PART_ENDINGS = ["co.uk", "org.uk", "me.uk", "ltd.uk", "plc.uk", "net.uk", "nhs.uk", "gov.uk", "ac.uk", "sch.uk"];

// The registrable domain: the website a business owns, whatever page or
// subdomain the address points at. Accepts a full address or a bare domain.
// "https://bookings.smartflowservices.co.uk/plumbing" -> "smartflowservices.co.uk"
// "shop.example.com" -> "example.com"
export function brandOf(address) {
  if (!address || typeof address !== "string") return null;
  const cleaned = address.split("›")[0].trim();
  if (!cleaned) return null;
  let host;
  try {
    host = new URL(/^[a-z]+:\/\//i.test(cleaned) ? cleaned : `https://${cleaned}`).hostname.toLowerCase();
  } catch (e) {
    return null;
  }
  host = host.replace(/^www\./, "").replace(/\.$/, "");
  const parts = host.split(".");
  if (parts.length < 2 || parts.some(p => !p)) return null;
  const endingParts = UK_TWO_PART_ENDINGS.includes(parts.slice(-2).join(".")) ? 2 : 1;
  if (parts.length <= endingParts) return null;
  return parts.slice(-(endingParts + 1)).join(".");
}

// The name part of a website: "bookings.smartflowservices.co.uk" -> "smartflowservices"
export function brandLabel(address) {
  const domain = brandOf(address);
  return domain ? domain.split(".")[0] : "";
}

// Matches a business name (map listing, LSA, Shopping seller) to a website
// we've seen in PPC or SEO. Used only when the listing has no readable website.
export function matchDomainByName(name, domains) {
  const full = compact(name);
  if (full.length < 5) return null;
  let best = null;
  for (const domain of domains) {
    const brand = brandLabel(domain);
    if (brand.length < 6) continue;
    if (full.includes(brand) || brand.includes(full)) {
      if (!best || brand.length > brandLabel(best).length) best = domain;
    }
  }
  return best;
}

export function businessName(item, channel) {
  const name = item._name && item._name !== "Unknown" ? item._name
    : channel === "gbp" ? item.title
    : channel === "lsa" ? (item.title || item.name)
    : (item.seller || item.merchant || item.title);
  return String(name || "Unknown").trim();
}
